import {
  APP_MODULES,
  ENVIRONMENT_LABELS,
  type ModuleDefinition,
  type Permission,
  reachesJobs,
  type WorklistSummary,
} from '@k-management/shared';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate, useNavigationType } from 'react-router';
import { useAuth, useCurrentUser } from '../auth/AuthProvider.js';
import { apiRequest } from '../lib/api.js';
import {
  applyTheme,
  forgetPreferences,
  THEME_KEY,
  type Theme,
  usePreference,
  useSessionValue,
} from '../lib/preferences.js';
import { Icon, type IconName } from './Icon.js';
import { PageChromeProvider, usePageChrome } from './layout/page-chrome.js';
import { ReleaseNotes } from './ReleaseNotes.js';

const MODULES: readonly ModuleDefinition[] = APP_MODULES;

const MODULE_ICONS: Record<string, IconName> = {
  anagrafica: 'users',
  preventivatore: 'calc',
  tabelle: 'table',
};

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** Announced, not available yet. */
  soon?: boolean;
  /** Shown only to who has this permission besides the module (a reserved function). */
  requires?: Permission;
  /** What waits there (1.73): shown next to the label when more than zero. */
  count?: number;
}

const ADMIN_PAGES: NavItem[] = [
  { to: '/admin/utenti', label: 'Utenti', icon: 'user' },
  { to: '/admin/ruoli', label: 'Profili di accesso', icon: 'shield' },
  { to: '/admin/backup', label: 'Backup', icon: 'database' },
  { to: '/admin/sharepoint', label: 'SharePoint', icon: 'upload' },
  { to: '/admin/gestify', label: 'Gestify', icon: 'database' },
  { to: '/admin/registro', label: 'Registro attività', icon: 'history' },
];

/** The pages of Organizzazione (03/10/2026), under their item in the menu. */
const ORGANIZATION_PAGES: NavItem[] = [
  { to: '/organizzazione/ruoli', label: 'Ruoli', icon: 'users' },
  { to: '/organizzazione/responsabilita', label: 'Responsabilità', icon: 'columns' },
  { to: '/organizzazione/regole', label: 'Regole', icon: 'book' },
];

/** The employees (07/10/2026), under Organizzazione for who has the module Persone. */
const PEOPLE_PAGE: NavItem = { to: '/organizzazione/persone', label: 'Persone', icon: 'user' };

/** Controllo di gestione (08/10/2026): the plans first; the account and the budget come next. */
const CONTROLLING_PAGES: NavItem[] = [
  { to: '/controllo/piani', label: 'Piani riclassificati', icon: 'columns' },
];

/** Report (2.33): the Dashboard first; the other reports come next. */
const REPORT_PAGES: NavItem[] = [
  { to: '/report/dashboard', label: 'Dashboard', icon: 'columns', requires: 'report.dashboard' },
];

/**
 * The area Commerciale (decision of 05/10/2026): the work in its order, from
 * the internal preventivo to the offer for the client. The commesse moved to
 * Operations (2.41).
 */
const COMMERCIAL_PAGES: NavItem[] = [
  { to: '/preventivatore', label: 'Preventivi', icon: 'calc' },
  { to: '/offerte', label: 'Offerte', icon: 'upload' },
];

/**
 * The area Operations (26/10/2026): the commesse with the clients and the
 * internal progetti; the progetti also for who has only an area of them.
 */
const COMMESSE_PAGE: NavItem = { to: '/commesse', label: 'Commesse', icon: 'briefcase' };
const PROJECTS_PAGE: NavItem = { to: '/progetti', label: 'Progetti', icon: 'columns' };

/**
 * The area Tesoreria (decision of 06/10/2026): one entry at a time, the
 * invoices first, issued and received in two entries (07/10/2026); the others
 * are announced.
 */
const TREASURY_PAGES: NavItem[] = [
  { to: '/tesoreria/da-sistemare', label: 'Da sistemare', icon: 'check' },
  { to: '/tesoreria/fatture-attive', label: 'Fatture attive', icon: 'up' },
  { to: '/tesoreria/fatture-passive', label: 'Fatture passive', icon: 'down' },
  { to: '/tesoreria/autofatture', label: 'Autofatture', icon: 'columns' },
  { to: '/tesoreria/mastrini', label: 'Mastrini', icon: 'book' },
  { to: '/tesoreria/situazione-iniziale', label: 'Situazione iniziale', icon: 'table' },
  { to: '/tesoreria/chiusura-anno', label: 'Chiusura dell’anno', icon: 'check' },
  { to: '/tesoreria/schede-contabili', label: 'Schede contabili', icon: 'wallet' },
  {
    to: '/tesoreria/anagrafica-dalle-fatture',
    label: 'Anagrafica dalle fatture',
    icon: 'user',
    requires: 'tesoreria.write',
  },
  { to: '/tesoreria/scadenzario', label: 'Scadenzario', icon: 'history' },
  { to: '/tesoreria/banche', label: 'Banche', icon: 'database' },
  { to: '/tesoreria/giroconti', label: 'Giroconti', icon: 'columns' },
  { to: '/tesoreria/anticipi', label: 'Anticipi fatture', icon: 'up' },
  { to: '/tesoreria/mutui', label: 'Mutui e finanziamenti', icon: 'briefcase' },
  { to: '/tesoreria/rateazioni', label: 'Rateazioni', icon: 'calc' },
  { to: '/tesoreria/f24', label: 'F24', icon: 'calc' },
  {
    to: '/tesoreria/carte',
    label: 'Carte di credito',
    icon: 'wallet',
    requires: 'tesoreria.cards',
  },
  { to: '/tesoreria/impegni', label: 'Altri impegni', icon: 'calc', soon: true },
  { to: '/tesoreria/previsioni', label: 'Previsioni', icon: 'columns', soon: true },
];

/** Title of a page when the page does not set one (tab and top bar). */
function defaultTitle(path: string): string {
  if (path === '/') return 'Home';
  const admin = ADMIN_PAGES.find((page) => path.startsWith(page.to));
  if (admin) return admin.label;
  if (path === '/progetti' || path.startsWith('/progetti/')) return 'Progetti';
  const module = MODULES.find((item) => path === item.path || path.startsWith(`${item.path}/`));
  return module?.label ?? 'K-Management';
}

/** Common frame (proposal §7): side menu, tabs of the open pages, top bar, command bar. */
export function AppLayout() {
  return (
    <PageChromeProvider>
      <Frame />
    </PageChromeProvider>
  );
}

interface Tab {
  path: string;
  title: string;
}

const MAX_TABS = 10;

function Frame() {
  const user = useCurrentUser();
  // The proposals of the Anagrafica from the invoices waiting (1.73), next to their entry.
  const proposals = useQuery({
    queryKey: ['treasury', 'registry-proposals', 'count'],
    queryFn: () => apiRequest<{ count: number }>('/treasury/registry-proposals/count'),
    enabled: user.permissions.includes('tesoreria.write'),
    staleTime: 60_000,
  });
  // What is still to settle in the Tesoreria (3.00): to do and late holds.
  const worklist = useQuery({
    queryKey: ['treasury', 'worklist', 'summary'],
    queryFn: () => apiRequest<WorklistSummary>('/treasury/worklist/summary'),
    enabled: user.permissions.includes('tesoreria.read'),
    staleTime: 60_000,
  });
  const { signOut, environment } = useAuth();
  const { title, commands } = usePageChrome();
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousPath = useRef<string | null>(null);
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = usePreference('menu.collapsed', false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [theme, setTheme] = usePreference<Theme>(THEME_KEY, 'system');
  // Open tabs and menus last while the browser tab stays open (2.36): reopened,
  // the app starts from the Home with every menu closed.
  const [tabs, setTabs] = useSessionValue<Tab[]>(`tabs.${user.email}`, []);
  const [openMenus, setOpenMenus] = useSessionValue<string[]>('menu.open', []);

  const path = location.pathname;
  const pageTitle = title ?? defaultTitle(path);
  const isTest = environment === 'test' || environment === 'development';

  useEffect(() => applyTheme(theme), [theme]);
  // Until 2.35 tabs and menus were kept for good in this browser.
  useEffect(() => forgetPreferences(['tabs.', 'menu.open.']), []);
  useEffect(() => setDrawerOpen(false), [path]);
  useEffect(() => {
    document.title = `${pageTitle} · K-Management`;
    // A redirect (e.g. /tabelle → /tabelle/priorita) replaces the tab of the page it left.
    const replaced =
      navigationType === 'REPLACE' && previousPath.current !== path ? previousPath.current : null;
    previousPath.current = path;
    const kept = replaced ? tabs.filter((tab) => tab.path !== replaced) : tabs;
    if (path === '/') {
      if (kept !== tabs) setTabs(kept);
      return;
    }
    const current = kept.find((tab) => tab.path === path);
    if (current?.title === pageTitle && kept === tabs) return;
    const next = current
      ? kept.map((tab) => (tab.path === path ? { path, title: pageTitle } : tab))
      : [...kept, { path, title: pageTitle }].slice(-MAX_TABS);
    setTabs(next);
  }, [path, pageTitle, tabs, setTabs, navigationType]);

  const closeTab = (closing: string) => {
    const index = tabs.findIndex((tab) => tab.path === closing);
    const rest = tabs.filter((tab) => tab.path !== closing);
    setTabs(rest);
    if (closing === path) navigate(rest[Math.min(index, rest.length - 1)]?.path ?? '/');
  };

  const closeAllTabs = () => {
    setTabs([]);
    navigate('/');
  };

  const modules = MODULES.filter((module) =>
    (user.permissions as readonly string[]).includes(`${module.key}.read`),
  );
  const management: NavItem[] = [
    { to: '/', label: 'Home', icon: 'home' },
    ...modules
      .filter(
        (module) =>
          module.key !== 'tabelle' &&
          module.key !== 'organizzazione' &&
          module.key !== 'persone' &&
          module.key !== 'controllo' &&
          module.key !== 'report' &&
          module.key !== 'preventivatore' &&
          module.key !== 'tesoreria' &&
          module.key !== 'commesse',
      )
      .map((module) => ({ to: module.path, label: module.label, icon: MODULE_ICONS[module.key]! })),
  ];
  const sees = (key: string) => modules.some((module) => module.key === key);
  // The progetti also by their areas or as their PM (ADR-025).
  const seesProjects = reachesJobs(user, 'read');
  const menuBases = [
    ...(sees('preventivatore') ? ['/commerciale'] : []),
    ...(sees('commesse') || seesProjects ? ['/operations'] : []),
    ...(sees('tesoreria') ? ['/tesoreria'] : []),
    ...(sees('controllo') ? ['/controllo'] : []),
    ...(sees('report') ? ['/report'] : []),
    ...(sees('organizzazione') || sees('persone') ? ['/organizzazione'] : []),
  ];
  const menu = (base: string) => ({
    base,
    expanded: openMenus.includes(base),
    onToggle: () =>
      setOpenMenus(
        openMenus.includes(base) ? openMenus.filter((open) => open !== base) : [...openMenus, base],
      ),
  });
  const configuration: NavItem[] = modules
    .filter((module) => module.key === 'tabelle')
    .map((module) => ({ to: module.path, label: module.label, icon: 'table' }));

  return (
    <div className={`shell${collapsed ? ' collapsed' : ''}${drawerOpen ? ' drawer-open' : ''}`}>
      <aside className="sidebar" aria-label="Menu principale">
        <Link to="/" className="sidebar-brand">
          <img src="/k-rev-logo.png" alt="K-Rev" width={83} height={36} />
          <span>Management</span>
        </Link>
        {menuBases.length > 0 && (
          <div className="menu-tools">
            <button
              type="button"
              aria-label="Chiudi tutti i menu"
              title="Chiudi tutti i menu"
              onClick={() => setOpenMenus([])}
            >
              <Icon name="chevron-up" size={16} />
            </button>
            <button
              type="button"
              aria-label="Apri tutti i menu"
              title="Apri tutti i menu"
              onClick={() => setOpenMenus(menuBases)}
            >
              <Icon name="chevron-down" size={16} />
            </button>
          </div>
        )}
        <NavGroup label="Gestione" items={management}>
          {sees('preventivatore') && (
            <NavParent
              label="Commerciale"
              icon="briefcase"
              {...menu('/commerciale')}
              paths={['/preventivatore', '/offerte']}
              items={COMMERCIAL_PAGES}
            />
          )}
          {(sees('commesse') || seesProjects) && (
            <NavParent
              label="Operations"
              icon="columns"
              {...menu('/operations')}
              paths={['/commesse', '/progetti']}
              items={[
                ...(sees('commesse') ? [COMMESSE_PAGE] : []),
                ...(seesProjects ? [PROJECTS_PAGE] : []),
              ]}
            />
          )}
          {sees('tesoreria') && (
            <NavParent
              label="Tesoreria"
              icon="wallet"
              {...menu('/tesoreria')}
              items={TREASURY_PAGES.filter(
                (item) => !item.requires || user.permissions.includes(item.requires),
              ).map((item) =>
                item.to === '/tesoreria/anagrafica-dalle-fatture'
                  ? { ...item, count: proposals.data?.count }
                  : item.to === '/tesoreria/da-sistemare'
                    ? { ...item, count: worklist.data?.total }
                    : item,
              )}
            />
          )}
          {sees('controllo') && (
            <NavParent
              label="Controllo di gestione"
              icon="calc"
              {...menu('/controllo')}
              items={CONTROLLING_PAGES}
            />
          )}
          {sees('report') && (
            <NavParent
              label="Report"
              icon="book"
              {...menu('/report')}
              items={REPORT_PAGES.filter(
                (item) => !item.requires || user.permissions.includes(item.requires),
              )}
            />
          )}
          {(sees('organizzazione') || sees('persone')) && (
            <NavParent
              label="Organizzazione"
              icon="org"
              {...menu('/organizzazione')}
              items={[
                ...(sees('organizzazione') ? ORGANIZATION_PAGES : []),
                ...(sees('persone') ? [PEOPLE_PAGE] : []),
              ]}
            />
          )}
        </NavGroup>
        {configuration.length > 0 && <NavGroup label="Configurazione" items={configuration} />}
        {user.isAdmin && <NavGroup label="Admin" items={ADMIN_PAGES} />}
        <div className="sidebar-footer">
          <ReleaseNotes />
        </div>
      </aside>
      <button
        type="button"
        className="scrim"
        aria-label="Chiudi il menu"
        onClick={() => setDrawerOpen(false)}
      />

      <div className="main">
        {isTest && environment && (
          <div className={`environment-banner ${environment}`} role="status">
            Ambiente di {ENVIRONMENT_LABELS[environment].toLowerCase()}: i dati non sono quelli
            reali
          </div>
        )}
        <header className="topbar">
          <button
            type="button"
            className="icon-button"
            aria-label="Apri o chiudi il menu"
            onClick={() => {
              if (window.matchMedia('(max-width: 820px)').matches) setDrawerOpen(!drawerOpen);
              else setCollapsed(!collapsed);
            }}
          >
            <Icon name="menu" />
          </button>
          <strong className="topbar-title">{pageTitle}</strong>
          <div className="segmented-icons" role="radiogroup" aria-label="Tema">
            {(
              [
                ['light', 'sun', 'Chiaro'],
                ['dark', 'moon', 'Scuro'],
                ['system', 'monitor', 'Come il sistema'],
              ] as const
            ).map(([value, icon, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={theme === value}
                aria-label={label}
                title={label}
                className={theme === value ? 'selected' : undefined}
                onClick={() => setTheme(value)}
              >
                <Icon name={icon} size={16} />
              </button>
            ))}
          </div>
          <div className="user">
            <span>
              {user.firstName} {user.lastName}
              <small>{user.isAdmin ? 'Amministratore' : user.email}</small>
            </span>
            <button
              type="button"
              className="icon-button"
              aria-label="Esci"
              title="Esci"
              onClick={() => void signOut()}
            >
              <Icon name="logout" />
            </button>
          </div>
        </header>

        <nav className="page-tabs" aria-label="Pagine aperte">
          <NavLink to="/" end className="page-tab">
            <Icon name="home" size={15} />
            <span>Home</span>
          </NavLink>
          {tabs.map((tab) => (
            <div key={tab.path} className={`page-tab${tab.path === path ? ' active' : ''}`}>
              <Link to={tab.path}>{tab.title}</Link>
              <button
                type="button"
                aria-label={`Chiudi ${tab.title}`}
                onClick={() => closeTab(tab.path)}
              >
                <Icon name="close" size={14} />
              </button>
            </div>
          ))}
          {tabs.length > 0 && (
            <button
              type="button"
              className="page-tabs-close"
              title="Chiudi tutte le schede e torna alla Home"
              onClick={closeAllTabs}
            >
              <Icon name="close" size={14} />
              <span>Chiudi tutte</span>
            </button>
          )}
        </nav>

        <div className="work">
          <main className="content">
            <Outlet />
          </main>
          {commands.length > 0 && (
            <aside className="command-bar" aria-label="Comandi della pagina">
              {commands.map((command) => (
                <button
                  key={command.key}
                  type="button"
                  className={command.primary ? 'command main-command' : 'command'}
                  disabled={command.disabled}
                  onClick={command.onClick}
                >
                  <Icon name={command.icon} />
                  <span>{command.label}</span>
                </button>
              ))}
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}

function NavGroup({
  label,
  items,
  children,
}: {
  label: string;
  items: NavItem[];
  children?: ReactNode;
}) {
  return (
    <div className="nav-group">
      <div className="nav-group-label">{label}</div>
      {items.map((item) =>
        item.soon ? (
          <span key={item.to} className="nav-item soon" title={`${item.label}: in arrivo`}>
            <Icon name={item.icon} />
            <span>{item.label}</span>
            <em>presto</em>
          </span>
        ) : (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className="nav-item"
            title={item.label}
          >
            <Icon name={item.icon} />
            <span>{item.label}</span>
          </NavLink>
        ),
      )}
      {children}
    </div>
  );
}

/**
 * An item of the menu that opens and closes its pages (Organizzazione,
 * 03/10/2026). Closed, it stays marked while one of its pages is open; the
 * frame keeps which items are open, to open or close them all (2.36).
 */
function NavParent({
  label,
  icon,
  base,
  paths = [base],
  items,
  expanded,
  onToggle,
}: {
  label: string;
  icon: IconName;
  /** Key of the open/closed state; also the path of the area when `paths` is missing. */
  base: string;
  /** Where the pages of the area live, when they are not all under `base`. */
  paths?: string[];
  items: NavItem[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const location = useLocation();
  const inside = paths.some(
    (path) => location.pathname === path || location.pathname.startsWith(`${path}/`),
  );
  return (
    <>
      <button
        type="button"
        className={`nav-item nav-parent${inside ? ' current' : ''}`}
        aria-expanded={expanded}
        title={label}
        onClick={onToggle}
      >
        <Icon name={icon} />
        <span>{label}</span>
        <span className="nav-chevron" aria-hidden="true">
          {expanded ? '▾' : '▸'}
        </span>
      </button>
      {expanded &&
        items.map((item) =>
          item.soon ? (
            <span
              key={item.to}
              className="nav-item nav-sub soon"
              title={`${item.label}: in arrivo`}
            >
              <Icon name={item.icon} size={16} />
              <span>{item.label}</span>
              <em>presto</em>
            </span>
          ) : (
            <NavLink key={item.to} to={item.to} className="nav-item nav-sub" title={item.label}>
              <Icon name={item.icon} size={16} />
              <span>{item.label}</span>
              {Boolean(item.count) && <em className="nav-count">{item.count}</em>}
            </NavLink>
          ),
        )}
    </>
  );
}
